import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { MenuItem } from '@openng/optimus-ui/api';
import { combineLatest, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { AuthorizationService } from '~ngx-shared/authentication';
import { MenuService } from '~ngx-shared/layout';
import { Aggregate } from '~ngx-shared/models';
import { UserService } from '~shared/services';
import { AuthorizationUtil, RoleEnum } from '~shared/utils';

@Injectable({
  providedIn: 'root'
})
export class PendingService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly userService = inject(UserService);
  private readonly menuService = inject(MenuService);
  private readonly apollo = inject(Apollo);

  subscriptions: Subscription[] = [];

  changeMenuItem = new Subject<MenuItem>();

  constructor() {
    this.destroyRef.onDestroy(() => this.subscriptions.forEach(s => s.unsubscribe()));
    this.changeMenuItem.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(item => {
      const menuItem = this.menuService.getItemFromRoute(item.routerLink);
      if (!menuItem) return;

      menuItem.badge = !!item.badge && !!Number(item.badge) ? String(item.badge) : undefined;
    });
  }

  init(): void {
    combineLatest([
      this.userService.user$.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()),
      this.authorizationService.currentRole$.pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged()
      )
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([user, role]) => {
        // Unsubscribe to clear old ones
        this.subscriptions.forEach(subscription => subscription.unsubscribe());

        if ((this.authorizationService.can(RoleEnum.MODERATOR) || user) && role) {
          const personId = user?.person_id || 0;

          // Every badge below counts what the current role has not voted on; a role that cannot
          // vote would only ever see it grow.
          if (this.authorizationService.can(RoleEnum.EDUCATION_OFFICE_ASSISTANT)) {
            return;
          }

          if (this.authorizationService.can(AuthorizationUtil.GROUP_INSPECTOR)) {
            this.subscriptions.push(
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationChildAllowanceApplicationApproval($personId: bigint!) {
                      result: education_child_allowance_application_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            child_allowance_application_approvals: {
                              person_id: { _eq: $personId }
                              role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'child-allowance-applications'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                })
            );

            this.subscriptions.push(
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                  subscription GetEductionTimetableApproval($personId: bigint!) {
                    result: education_latest_timetable_data_aggregate(
                      where: {
                        approval_status: { _eq: pending }
                        timetable: { school_period: { is_active: { _eq: true } } }
                        _not: {
                          timetable_approvals: {
                            person_id: { _eq: $personId }
                            role_name: { _eq: "${role}" }
                          }
                        }
                      }
                    ) {
                      aggregate {
                        count
                      }
                    }
                  }
                `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'timetables'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                })
            );

            this.subscriptions.push(
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationAbsenceApproval($personId: bigint!) {
                      result: education_latest_absence_data_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          absence: {
                            person_id: { _neq: ${personId} }
                            _or: [{ current_teacher_data: {} }, { current_inspector_data: {} }]
                          }
                          _not: {
                            absence_approvals: {
                              person_id: { _eq: $personId }
                              role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'absences'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                })
            );

            this.subscriptions.push(
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationTransferServiceApproval($personId: bigint!) {
                      result: education_transfer_service_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            transfer_service_approvals: {
                              person_id: { _eq: $personId }
                              role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'transfer-services'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                })
            );

            this.subscriptions.push(
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationIgazasApproval($personId: bigint!) {
                      result: education_addendum_igaza_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            addendum_igaza_approvals: {
                              person_id: { _eq: $personId }
                              role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'addendum-igazas'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                })
            );
          }

          if (this.authorizationService.can(AuthorizationUtil.GROUP_SCHOOL_INSPECTOR)) {
            this.subscriptions.push(
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationPlanningFormApproval($personId: bigint!) {
                      result: education_planning_form_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            planning_form_approvals: {
                              person_id: { _eq: $personId }
                              role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'planning-forms'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                })
            );

            this.subscriptions.push(
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationPartTimeJobApproval($personId: bigint!) {
                      result: education_current_part_time_job_data_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            part_time_job_approvals: {
                              person_id: { _eq: $personId }
                              role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'part-time-jobs'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                })
            );
          }

          if (this.authorizationService.can(AuthorizationUtil.GROUP_EDUCATION_WITHOUT_ASSISTANT)) {
            this.subscriptions.push(
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationTerminationOfServiceApproval($personId: bigint!) {
                      result: education_termination_of_service_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            termination_of_service_approvals: {
                              person_id: { _eq: $personId }
                               role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'termination-of-services'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                }),
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationIgazaCertificateApproval($personId: bigint!) {
                      result: education_igaza_certificate_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            igaza_certificate_approvals: {
                              person_id: { _eq: $personId }
                               role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'igaza-certificates'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                })
            );
          }

          if (this.authorizationService.can(AuthorizationUtil.GROUP_SCHOOL)) {
            this.subscriptions.push(
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationDeclarationOfCommitmentIlPdApproval($personId: bigint!) {
                      result: education_declaration_of_commitment_il_pd_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            declaration_of_commitment_il_pd_approvals: {
                              person_id: { _eq: $personId }
                               role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'declaration-of-commitment-il-pds'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                }),
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationDeclarationOfCommitmentApproval($personId: bigint!) {
                      result: education_declaration_of_commitment_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            declaration_of_commitment_approvals: {
                              person_id: { _eq: $personId }
                               role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'declaration-of-commitments'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                }),
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationServiceContractApproval($personId: bigint!) {
                      result: education_service_contract_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            service_contract_approvals: {
                              person_id: { _eq: $personId }
                               role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'service-contracts'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                })
            );
          }

          if (this.authorizationService.can(AuthorizationUtil.GROUP_EDUCATION)) {
            this.subscriptions.push(
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationProposalHonorApproval($personId: bigint!) {
                      result: education_proposal_honor_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: { proposal_honor_approvals: { person_id: { _eq: $personId } } }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'proposal-honors'],
                    badge: String(queryResult.data?.result?.aggregate?.count)
                  });
                })
            );
          }

          if (this.authorizationService.can(RoleEnum.TEACHER)) {
            this.subscriptions.push(
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationObservationSheetFormApproval {
                      result: education_observation_sheet_form_aggregate(
                        where: { approval_status: { _eq: pending } }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'my', 'observation-sheets'],
                    badge: queryResult.data?.result?.aggregate?.count
                      ? String(queryResult.data?.result?.aggregate?.count)
                      : undefined
                  });
                }),
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationServiceContractApproval($personId: bigint!) {
                      result: education_service_contract_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            service_contract_approvals: {
                              person_id: { _eq: $personId }
                               role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'my', 'service-contracts'],
                    badge: queryResult.data?.result?.aggregate?.count
                      ? String(queryResult.data?.result?.aggregate?.count)
                      : undefined
                  });
                }),
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationDeclarationOfCommitmentIlPdApproval($personId: bigint!) {
                      result: education_declaration_of_commitment_il_pd_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            declaration_of_commitment_il_pd_approvals: {
                              person_id: { _eq: $personId }
                               role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'my', 'declaration-of-commitment-il-pds'],
                    badge: queryResult.data?.result?.aggregate?.count
                      ? String(queryResult.data?.result?.aggregate?.count)
                      : undefined
                  });
                }),
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationDeclarationOfCommitmentApproval($personId: bigint!) {
                      result: education_declaration_of_commitment_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            declaration_of_commitment_approvals: {
                              person_id: { _eq: $personId }
                               role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'my', 'declaration-of-commitments'],
                    badge: queryResult.data?.result?.aggregate?.count
                      ? String(queryResult.data?.result?.aggregate?.count)
                      : undefined
                  });
                }),
              this.apollo
                .subscribe<{ result?: Aggregate }>({
                  query: gql`
                    subscription GetEducationChildAllowanceApplicationSignature($personId: bigint!) {
                      result: education_child_allowance_application_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            child_allowance_application_approvals: {
                              person_id: { _eq: $personId }
                              role_name: { _eq: "${role}" }
                              signature_verified: { _eq: true }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                  variables: {
                    personId
                  }
                })
                .subscribe(queryResult => {
                  this.changeMenuItem.next({
                    routerLink: ['/', 'my', 'child-allowance-applications'],
                    badge: queryResult.data?.result?.aggregate?.count
                      ? String(queryResult.data?.result?.aggregate?.count)
                      : undefined
                  });
                })
            );
          }
        }
      });
  }
}
